import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc, serverTimestamp, getDoc, setDoc, addDoc } from '../firebase';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { notify } from '../services/notify';
import { CORE_CHART_OF_ACCOUNTS } from '../constants';
import { Loader2, Plus, Save } from 'lucide-react';

const getLocalAuthToken = () => localStorage.getItem('app_auth_token');
const isLocalSession = () => Boolean(getLocalAuthToken());

const localApiRequest = async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
  const response = await fetch(path, {
    ...init,
    headers: {
      Authorization: `Bearer ${getLocalAuthToken()}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw new Error(payload?.error || `Request failed (${response.status})`);
  return payload as T;
};

export default function DefaultAccountsView({ orgId, language }: { orgId: string, language: string }) {
  const [accounts, setAccounts] = useState<any[]>([]);
  const [settings, setSettings] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const fields = [
    { key: 'salesAccount', label: 'Sales Account' },
    { key: 'accountsReceivable', label: 'Accounts Receivable' },
    { key: 'accountsPayable', label: 'Accounts Payable' },
    { key: 'inventoryAccount', label: 'Inventory Account' },
    { key: 'cogsAccount', label: 'Cost of Goods Sold (COGS)' },
    { key: 'purchaseAccount', label: 'Purchase Account' },
    { key: 'vatInput', label: 'VAT Input (Purchases)' },
    { key: 'vatOutput', label: 'VAT Output (Sales)' },
    { key: 'vatPayable', label: 'VAT Payable/Settlement' },
    { key: 'bankCash', label: 'Default Bank/Cash Account' },
    { key: 'bankAccount', label: 'Secondary Bank Account' },
    { key: 'cashAccount', label: 'Secondary Cash Account' },
    { key: 'salesReturns', label: 'Sales Returns Account' },
    { key: 'purchaseReturns', label: 'Purchase Returns Account' },
    { key: 'discountGiven', label: 'Discount Given Account' },
    { key: 'discountReceived', label: 'Discount Received Account' },
    { key: 'deliveryInAccount', label: 'Delivery In Account' },
    { key: 'deliveryOutAccount', label: 'Delivery Out Account' }
  ];

  const suggestDefaultAccounts = (availableAccounts: any[]) => {
    setSettings((prev: any) => {
      const newSettings = { ...prev };
      let changed = false;
      const codeMapping: Record<string, string> = {
        salesAccount: '4010', accountsReceivable: '1100', accountsPayable: '2010',
        inventoryAccount: '1200', cogsAccount: '5010', vatInput: '1400', vatOutput: '2100',
        vatPayable: '2110', bankCash: '1020', bankAccount: '1020', cashAccount: '1010',
        purchaseAccount: '5020', salesReturns: '4040', purchaseReturns: '5030',
        discountGiven: '4030', discountReceived: '6140', deliveryInAccount: '6020',
        deliveryOutAccount: '6020'
      };

      fields.forEach(field => {
        if (!newSettings[field.key]) {
          const defaultCode = codeMapping[field.key];
          const match = availableAccounts.find((account: any) =>
            account.code?.toString() === defaultCode ||
            account.name.toLowerCase().includes(field.label.toLowerCase().replace('default ', ''))
          );
          if (match) {
            newSettings[field.key] = match.id;
            changed = true;
          }
        }
      });
      return changed ? newSettings : prev;
    });
  };

  const seedBasicAccounts = async () => {
    if (!orgId) return;
    setSeeding(true);
    try {
      if (isLocalSession()) {
        for (const coreAcc of CORE_CHART_OF_ACCOUNTS) {
          if (accounts.some(account => account.code === coreAcc.code)) continue;
          await localApiRequest(`/api/organizations/${orgId}/chart-of-accounts`, {
            method: 'POST',
            body: JSON.stringify({ ...coreAcc, status: 'active', isSystemAccount: true })
          });
        }
        const refreshed = await localApiRequest<any[]>(`/api/organizations/${orgId}/chart-of-accounts`);
        const sortedAccounts = [...refreshed].sort((left, right) => (left.code || '').localeCompare(right.code || ''));
        setAccounts(sortedAccounts);
        suggestDefaultAccounts(sortedAccounts);
      } else {
        const coaRef = collection(db, 'chart_of_accounts');
        for (const coreAcc of CORE_CHART_OF_ACCOUNTS) {
          const match = accounts.find(a => a.code === coreAcc.code);
          if (!match) {
            await addDoc(coaRef, {
              ...coreAcc,
              orgId,
              status: 'active',
              createdAt: serverTimestamp()
            });
          }
        }
      }
      notify.confirm('System accounts seeded successfully');
    } catch (err) {
      console.error(err);
      notify.error(err instanceof Error ? err.message : 'Failed to seed accounts');
    } finally {
      setSeeding(false);
    }
  };

  useEffect(() => {
    if (!orgId) return;

    if (isLocalSession()) {
      let cancelled = false;
      setLoading(true);
      void Promise.all([
        localApiRequest<any>(`/api/organizations/${orgId}/settings`),
        localApiRequest<any[]>(`/api/organizations/${orgId}/chart-of-accounts`),
      ])
        .then(([storedSettings, loadedAccounts]) => {
          if (cancelled) return;
          const sortedAccounts = [...loadedAccounts].sort((left, right) => (left.code || '').localeCompare(right.code || ''));
          setAccounts(sortedAccounts);
          setSettings(storedSettings?.default_accounts ?? storedSettings?.defaultAccounts ?? {});
          suggestDefaultAccounts(sortedAccounts);
        })
        .catch(error => {
          console.error('Local API error loading default accounts:', error);
          if (!cancelled) notify.error(error instanceof Error ? error.message : 'Failed to load default accounts');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
      return () => { cancelled = true; };
    }
    
    let isMounted = true;

    const fetchSettings = async () => {
      try {
        const settingsRef = doc(db, 'settings', orgId);
        const settingsSnap = await getDoc(settingsRef);
        if (settingsSnap.exists() && isMounted) {
          const data = settingsSnap.data();
          setSettings(data?.defaultAccounts || {});
        }
      } catch (err) {
        console.error("Error fetching settings:", err);
      }
    };

    const q = query(
      collection(db, 'chart_of_accounts'),
      where('orgId', '==', orgId),
      where('status', '==', 'active')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!isMounted) return;
      const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a: any, b: any) => (a.code || '').localeCompare(b.code || ''));
      setAccounts(docs);
      
      // Auto-populate mappings from standard account codes when available.
      suggestDefaultAccounts(docs);
      
      setLoading(false);
    }, (error) => {
      console.error("Error fetching accounts:", error);
      setLoading(false);
    });

    fetchSettings();
    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [orgId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isLocalSession()) {
        const current = await localApiRequest<any>(`/api/organizations/${orgId}/settings`);
        await localApiRequest(`/api/organizations/${orgId}/settings`, {
          method: 'PUT',
          body: JSON.stringify({
            defaultCurrency: current?.default_currency ?? current?.defaultCurrency ?? 'USD',
            defaultPaymentTerms: current?.default_payment_terms ?? current?.defaultPaymentTerms ?? null,
            allowedCurrencies: current?.allowed_currencies ?? current?.allowedCurrencies ?? [],
            defaultAccounts: settings,
            payroll: current?.payroll ?? {},
            data: current?.data ?? {},
          })
        });
      } else {
        const settingsRef = doc(db, 'settings', orgId);
        const settingsSnap = await getDoc(settingsRef);
        if (settingsSnap.exists()) {
          await updateDoc(settingsRef, {
            defaultAccounts: settings,
            updatedAt: serverTimestamp()
          });
        } else {
          await setDoc(settingsRef, {
            orgId,
            defaultAccounts: settings,
            updatedAt: serverTimestamp()
          });
        }
      }
      notify.confirm('Default accounts saved successfully');
    } catch (err) {
      if (isLocalSession()) {
        console.error('Local API error saving default accounts:', err);
        notify.error(err instanceof Error ? err.message : 'Failed to save default accounts');
      } else {
        handleFirestoreError(err, OperationType.UPDATE, 'settings');
        notify.error('Failed to save default accounts');
      }
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key: string, value: string) => {
    setSettings((prev: any) => ({ ...prev, [key]: value }));
  };

  if (loading) return (
    <div className="flex items-center justify-center p-20 animate-pulse">
      <Loader2 className="w-8 h-8 animate-spin text-primary mr-3" />
      <span className="text-sm font-bold uppercase tracking-widest opacity-50">Syncing Ledgers...</span>
    </div>
  );

  return (
    <div className="p-6 space-y-6 max-w-7xl animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 p-8 bg-white border border-[#141414] rounded-[2.5rem] shadow-xl">
        <div className="space-y-1">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-3 h-3 rounded-full bg-primary animate-ping" />
            <span className="text-[10px] font-bold uppercase tracking-[0.3em] opacity-40">Financial Core</span>
          </div>
          <h1 className="text-5xl font-black tracking-tighter text-[#141414] uppercase leading-none">Default Accounts</h1>
          <p className="text-xs font-mono text-[#141414]/60 uppercase tracking-widest pt-2">Map automated system actions to your general ledger</p>
        </div>
        <div className="flex items-center gap-3 w-full md:w-auto">
          <button
            onClick={seedBasicAccounts}
            disabled={seeding || saving}
            className="flex-1 md:flex-none border border-[#141414] text-[#141414] px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-black hover:text-white transition-all flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Seed Missing Accounts
          </button>
          <button
            onClick={handleSave}
            disabled={saving || seeding}
            className="flex-1 md:flex-none bg-[#141414] text-white px-8 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest hover:opacity-90 active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-2xl shadow-primary/20"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Sync & Save
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {fields.map(field => (
          <div key={field.key} className="bg-white p-6 rounded-3xl border border-[#141414]/10 hover:border-primary/20 hover:shadow-xl transition-all group">
            <div className="flex items-center justify-between mb-3">
              <label className="text-[10px] font-black uppercase tracking-widest opacity-40 group-hover:opacity-100 transition-opacity">{field.label}</label>
              {settings[field.key] ? (
                <div className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]" />
              ) : (
                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              )}
            </div>
            <select
              value={settings[field.key]?.toString() || ''}
              onChange={(e) => updateSetting(field.key, e.target.value)}
              className="w-full bg-[#141414]/5 border-none p-4 rounded-2xl outline-none focus:ring-2 focus:ring-primary/20 transition-all font-bold text-xs uppercase tracking-tight"
            >
              <option value="">-- NO ACCOUNT MAPPED --</option>
              {accounts.map(acc => (
                <option key={acc.id} value={acc.id}>
                  {acc.code} | {acc.name}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>

      {accounts.length === 0 && !loading && (
        <div className="p-20 text-center bg-red-50 border-2 border-dashed border-red-200 rounded-[3rem] animate-in zoom-in duration-500">
           <h3 className="text-2xl font-black text-red-900 uppercase tracking-tighter mb-2">No active accounts found</h3>
           <p className="text-sm text-red-700/60 font-medium max-w-md mx-auto mb-6">
             You need to have active accounts in your Chart of Accounts before they can be mapped here.
           </p>
           <button 
             onClick={seedBasicAccounts}
             className="px-8 py-4 bg-red-600 text-white rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-red-700 transition-all shadow-xl shadow-red-600/20"
           >
             Initialize System Accounts
           </button>
        </div>
      )}
    </div>
  );
}
